import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, Mic, MicOff, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface InlineCallInterfaceProps {
  isConnecting: boolean;
  isConnected: boolean;
  isMuted: boolean;
  volumeLevel: number;
  isAssistantSpeaking: boolean;
  onStartCall: () => void;
  onEndCall: () => void;
  onToggleMute: () => void;
  disabled?: boolean;
}

export function InlineCallInterface({
  isConnecting,
  isConnected,
  isMuted,
  volumeLevel,
  isAssistantSpeaking,
  onStartCall,
  onEndCall,
  onToggleMute,
  disabled
}: InlineCallInterfaceProps) {
  const isActive = isConnecting || isConnected;

  return (
    <AnimatePresence mode="wait">
      {!isActive ? (
        <motion.div
          key="button"
          initial={{ opacity: 0, filter: "blur(4px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          exit={{ opacity: 0, filter: "blur(4px)" }}
          transition={{ duration: 0.15 }}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={onStartCall}
            disabled={disabled}
            className="gap-2 hover:bg-accent hover:border-primary/20 transition-colors"
          >
            <Phone className="h-4 w-4" />
            Talk to Assistant
          </Button>
        </motion.div>
      ) : (
        <motion.div
          key="interface"
          initial={{ opacity: 0, filter: "blur(4px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          exit={{ opacity: 0, filter: "blur(4px)" }}
          transition={{ duration: 0.15 }}
          className={cn(
            "flex items-center gap-2 px-3 h-9 rounded-md border transition-all duration-300 min-w-[240px]",
            isConnected 
              ? "bg-primary/5 border-primary/20 dark:bg-primary/10" 
              : "bg-muted/50 border-border"
          )}
        >
          {/* Live indicator */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <motion.div
                animate={{ 
                  scale: isConnecting ? [1, 1.1, 1] : 1,
                  opacity: isConnecting ? [1, 0.5, 1] : 1
                }}
                transition={{ 
                  duration: 1.5, 
                  repeat: isConnecting ? Infinity : 0,
                  ease: "easeInOut" 
                }}
              >
                <Radio className={cn(
                  "h-4 w-4 transition-colors duration-300",
                  isConnected ? "text-primary" : "text-muted-foreground"
                )} />
              </motion.div>
              {isConnected && (
                <motion.div
                  className="absolute inset-0"
                  initial={{ scale: 1, opacity: 1 }}
                  animate={{ 
                    scale: [1, 1.5, 1.5], 
                    opacity: [1, 0, 0] 
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeOut"
                  }}
                >
                  <Radio className="h-4 w-4 text-primary" />
                </motion.div>
              )}
            </div>
        <AnimatePresence mode="wait">
          <motion.span
            key={isConnecting ? "connecting" : "live"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "text-xs font-medium block",
              isConnected ? "text-primary" : "text-muted-foreground"
            )}
          >
            {isConnecting ? "Connecting..." : "Live"}
          </motion.span>
        </AnimatePresence>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

          {/* Control buttons */}
          <div className="flex items-center gap-1">
            <AnimatePresence>
              {isConnected && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15 }}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onToggleMute}
                    className={cn(
                      "h-8 w-8 transition-colors",
                      isMuted && "text-destructive hover:text-destructive"
                    )}
                  >
                    {isMuted ? (
                      <MicOff className="h-4 w-4" />
                    ) : (
                      <Mic className="h-4 w-4" />
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={onEndCall}
              disabled={isConnecting}
              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <PhoneOff className="h-4 w-4" />
            </Button>
          </div>

          {/* Speaking indicator - subtle pulse effect */}
          {isConnected && (isAssistantSpeaking || volumeLevel > 0.1) && (
            <motion.div
              className={cn(
                "absolute inset-0 rounded-md pointer-events-none",
                isAssistantSpeaking ? "bg-primary/10" : "bg-blue-500/10"
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.3, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
} 